import { setupOTelSDK } from '@cof/opentelemetry-instrumentation-rum';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';

import './index.scss';
import App from './App.tsx';
import { ErrorProvider } from './contexts/errorContext';
import { ThemeProvider } from './contexts/themeContext';
import { ToastProvider } from './contexts/toastContext';
import { getOtelAppEnvironment, getOtelGatewayBase } from './utils/environment';
import { initializeNewRelic } from './utils/newrelic';

// Initialize New Relic as early as possible (retained alongside OTel RUM).
initializeNewRelic();

// Initialize OpenTelemetry RUM. Wrapped so a telemetry failure can never block app render.
const appEnvironment = getOtelAppEnvironment(window.location.host);

if (appEnvironment !== 'local') {
  const gatewayBase = getOtelGatewayBase(appEnvironment);

  try {
    setupOTelSDK({
      asv: 'ASVAIRKEYEXTERNAL',
      ba: 'BAAirKeyExternal',
      component: 'AIRKEYSOFTWAREDISTRIBUTIONWEB',
      otelServiceName: `airkey-software-distribution-web-portal-${appEnvironment}`,
      overrides: {
        otelMetricsUrl: `${gatewayBase}/v1/metrics`,
        otelTracesUrl: `${gatewayBase}/v1/traces`,
        customResources: {
          TRACES_TARGET: 'card',
          METRICS_TARGET: 'card',
          'c1.instrumentation.type': 'browser',
        },
      },
    });
  } catch (error) {
    console.error('OTel RUM initialization failed:', error);
  }
}

const rootElement = document.getElementById('root');
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

if (rootElement) {
  createRoot(rootElement).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <ErrorProvider>
            <ToastProvider>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </ToastProvider>
          </ErrorProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
} else {
  console.error('Root element not found');
}
